import type { ComponentType } from "react";

import { V2BreadcrumbHeader, type IconWithSizeProps } from "@stacklet/ui";
import { useFragment } from "react-relay";
import { useLocation } from "react-router";
import { graphql } from "relay-runtime";

import { useConfig } from "app/contexts";

import type { BreadCrumbsHeader_query$key } from "./__generated__/BreadCrumbsHeader_query.graphql";

export interface BreadCrumbElement {
  label?: string;
  target?: string;
  tabs?: {
    label: string;
    target: string;
    linkTarget?: string;
    /** Optional chip text shown next to the label in the tab rail */
    chip?: string;
    /** Optional section heading grouping this tab in the settings rail */
    section?: string;
    /** Optional leading icon shown next to the label in the tab rail */
    icon?: ComponentType<IconWithSizeProps>;
  }[];
}

interface Props {
  queryRef: BreadCrumbsHeader_query$key;
  breadcrumbs: BreadCrumbElement[];
  currentPageTitle?: string;
}

export function BreadCrumbsHeader({
  queryRef,
  breadcrumbs,
  currentPageTitle,
}: Props) {
  const config = useConfig();
  const supportEnabled = !!config && !config.is_poc;
  const location = useLocation();

  const data = useFragment(
    graphql`
      fragment BreadCrumbsHeader_query on Query {
        whoami {
          email
          displayName
        }
        UrlConfig {
          docs
        }
      }
    `,
    queryRef,
  );

  const { displayName, email } = data.whoami ?? {};
  const docs = data.UrlConfig?.docs;

  const baseCrumbs = breadcrumbs.flatMap((crumb) => {
    if (Array.isArray(crumb.tabs)) {
      const activeTab = crumb.tabs.find(
        (t) => t.target && location.pathname.startsWith(t.target),
      );
      return activeTab
        ? [
            {
              label: activeTab.label,
              target: activeTab.linkTarget ?? activeTab.target,
            },
          ]
        : [];
    }
    if (crumb.label) {
      return [{ label: crumb.label, target: crumb.target }];
    }
    return [];
  });

  const lastCrumb = baseCrumbs.at(-1);
  const isOnSubPage =
    currentPageTitle &&
    lastCrumb?.target &&
    lastCrumb.target !== location.pathname &&
    currentPageTitle !== lastCrumb.label;

  const v2Breadcrumbs = isOnSubPage
    ? [...baseCrumbs, { label: currentPageTitle }]
    : baseCrumbs;

  return (
    <div className="sticky top-0 z-10 mb-xl mt-xs">
      <V2BreadcrumbHeader
        breadcrumbs={v2Breadcrumbs}
        docsUrl={docs ?? ""}
        email={email ?? ""}
        pathname={location.pathname}
        supportEnabled={supportEnabled}
        username={displayName || email || ""}
      />
    </div>
  );
}
